import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type CalendarFollowupItemDto,
  captureMeetingKnowledge,
  dispatchCalendarFollowup,
  draftCalendarFollowup,
  getCalendarFollowupConfig,
  getCalendarFollowups,
  serverErrorMessage,
} from '../lib/api.js';
import { formatRelative } from '../lib/format.js';
import { AiEvidenceStrip } from './ui/AiEvidenceStrip.js';

/**
 * batch-292 (calendar Phase 3a) — the post-meeting follow-up hub on the Today home.
 * Recent past meetings (≤14d) with a linked contact: generate an AI follow-up draft
 * on-demand, edit it, and SEND (operator-confirmed, gated). Renders NOTHING when the
 * feature is off or there's nothing to follow up on.
 */
export function CalendarFollowups() {
  const cfg = useQuery({
    queryKey: ['calendar-followup-config'],
    queryFn: ({ signal }) => getCalendarFollowupConfig({ signal }),
    staleTime: 60_000,
  });
  // Adaptive poll: fast (4s) only while a draft is in flight, slow (30s) otherwise — the
  // hub mounts on the default Today home, so an always-fast poll of this multi-join feed
  // would be wasteful (review finding 4).
  const [draftingKeys, setDraftingKeys] = useState<Set<string>>(() => new Set());
  const reportDrafting = useCallback((key: string, on: boolean) => {
    setDraftingKeys((prev) => {
      if (on === prev.has(key)) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const list = useQuery({
    queryKey: ['calendar-followups'],
    queryFn: ({ signal }) => getCalendarFollowups({ signal }),
    enabled: cfg.data?.enabled === true,
    refetchInterval: cfg.data?.enabled ? (draftingKeys.size > 0 ? 4000 : 30000) : false,
  });

  if (!cfg.data?.enabled) return null;
  if (list.isError) {
    return (
      <section className="today-meetings" data-testid="calendar-followups">
        <p className="today-meetings-flag" role="alert">
          Couldn’t load follow-ups.
        </p>
      </section>
    );
  }
  const items = list.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <section
      className="today-meetings"
      data-testid="calendar-followups"
      aria-label="Post-meeting follow-ups"
    >
      <header className="today-meetings-head">
        <h2 className="today-meetings-title">Follow-ups</h2>
      </header>
      <ul className="today-meetings-list">
        {items.map((it) => (
          <FollowupRow
            key={`${it.event_id}:${it.contact_id}`}
            item={it}
            sendEnabled={cfg.data?.send_enabled === true}
            onDrafting={reportDrafting}
          />
        ))}
      </ul>
    </section>
  );
}

function FollowupRow({
  item,
  sendEnabled,
  onDrafting,
}: {
  item: CalendarFollowupItemDto;
  sendEnabled: boolean;
  onDrafting: (key: string, on: boolean) => void;
}) {
  const qc = useQueryClient();
  const rowKey = `${item.event_id}:${item.contact_id}`;
  const [edited, setEdited] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sentLocal, setSentLocal] = useState(false);
  // The draft_id this row is waiting to change away from — so Redraft/Retry (which start
  // from a non-null draft) also show "Drafting…" until the NEW draft lands, not just the
  // first draft (review finding 2).
  const claimRef = useRef<string | null>(null);

  const draftM = useMutation({
    mutationFn: () => draftCalendarFollowup(item.event_id, item.contact_id),
    onSuccess: () => {
      setErr(null);
      claimRef.current = item.draft_id ?? null;
      setRequested(true);
      setEdited(null);
      void qc.invalidateQueries({ queryKey: ['calendar-followups'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'draft failed')),
  });
  const sendM = useMutation({
    mutationFn: () =>
      dispatchCalendarFollowup(item.draft_id as string, edited ?? item.draft_text ?? ''),
    onSuccess: () => {
      setSentLocal(true);
      void qc.invalidateQueries({ queryKey: ['calendar-followups'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'send failed')),
  });
  // batch-296 — capture durable facts from this meeting into the contact's Knowledge queue
  // (async; the operator reviews under the contact's Knowledge tab). Fire-and-confirm.
  // Capture keeps its OWN error slot so a successful capture can't clear (and a capture
  // failure can't overwrite) a still-unresolved draft/send error in the same row (review).
  const [captured, setCaptured] = useState(false);
  const [captureErr, setCaptureErr] = useState<string | null>(null);
  const captureM = useMutation({
    mutationFn: () => captureMeetingKnowledge(item.event_id, item.contact_id),
    onSuccess: () => {
      setCaptureErr(null);
      setCaptured(true);
    },
    onError: (e) => setCaptureErr(serverErrorMessage(e, 'capture failed')),
  });

  const when = item.start_at ? formatRelative(item.start_at) : '';
  const text = edited ?? item.draft_text ?? '';
  const sent = item.sent || sentLocal;
  // Drafting while the POST is in flight, OR after it succeeds until the draft_id changes
  // away from what we captured (a fresh draft landed). Works for first-draft (captured
  // null → null) AND redraft/retry (captured the old id → wait for a new one).
  const drafting = draftM.isPending || (requested && (item.draft_id ?? null) === claimRef.current);

  useEffect(() => {
    onDrafting(rowKey, drafting);
  }, [drafting, rowKey, onDrafting]);
  useEffect(() => () => onDrafting(rowKey, false), [rowKey, onDrafting]);

  return (
    <li className="today-meeting">
      <span className="today-meeting-time">{when}</span>
      <span className="today-meeting-body">
        <span className="today-meeting-subject">
          {item.subject ?? 'Meeting'}
          <span className="unified-side-note"> · {item.contact_name ?? 'Contact'}</span>
        </span>
        {sent ? (
          <span className="unified-side-note">✓ Follow-up sent</span>
        ) : drafting ? (
          <span className="unified-side-note">Drafting…</span>
        ) : item.draft_status === 'ready' && item.draft_id ? (
          <>
            <AiEvidenceStrip confidence={item.draft_confidence} factsUsed={item.draft_facts_used} />
            <textarea
              value={text}
              onChange={(e) => setEdited(e.target.value)}
              rows={3}
              className="w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-[12px] text-zinc-100 outline-none focus:border-zinc-500"
              aria-label={`Follow-up to ${item.contact_name ?? 'contact'}`}
            />
            <span className="flex flex-wrap gap-2">
              {sendEnabled ? (
                <button
                  type="button"
                  className="today-meeting-contact is-flagged"
                  disabled={sendM.isPending || !text.trim()}
                  onClick={() => sendM.mutate()}
                >
                  {sendM.isPending ? 'Sending…' : 'Send'}
                </button>
              ) : (
                <span className="unified-side-note">review-only (sending off)</span>
              )}
              <button
                type="button"
                className="today-meeting-contact"
                disabled={draftM.isPending}
                onClick={() => draftM.mutate()}
              >
                ✨ Redraft
              </button>
            </span>
          </>
        ) : item.draft_status === 'suppressed' ? (
          <span className="unified-side-note">
            skipped: {item.draft_reason ?? 'no follow-up needed'} ·{' '}
            <button
              type="button"
              className="today-meeting-contact"
              disabled={draftM.isPending}
              onClick={() => draftM.mutate()}
            >
              Redraft
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="today-meeting-contact"
            disabled={draftM.isPending}
            onClick={() => draftM.mutate()}
          >
            {item.draft_status === 'failed' ? 'Retry draft' : '✍ Draft follow-up'}
          </button>
        )}
        <span className="today-meeting-capture">
          {captured ? (
            <span className="unified-side-note">
              🧠 Sent to {item.contact_name ?? 'the contact'}’s Knowledge queue — review there
            </span>
          ) : (
            <button
              type="button"
              className="today-meeting-contact"
              disabled={captureM.isPending}
              onClick={() => captureM.mutate()}
              title="Extract durable facts from this meeting into the contact's Knowledge review queue"
            >
              {captureM.isPending ? 'Capturing…' : '🧠 Capture knowledge'}
            </button>
          )}
          {captureErr ? (
            <span className="unified-side-note" role="alert">
              {' '}
              ⚠ {captureErr}
            </span>
          ) : null}
        </span>
        {err ? (
          <span className="unified-side-note" role="alert">
            ⚠ {err}
          </span>
        ) : null}
      </span>
    </li>
  );
}
